import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { getStoredAccountId } from '../account';

/** Minimum Auth0 settings every app feeds in. */
export interface Auth0Settings {
  domain: string;
  clientId: string;
  audience: string;
  /**
   * App identifier the Auth0 PostLogin Action uses to gate the JWT to a
   * specific subscribed app — `'tms'`, `'directory'`, `'depot'`, `'expenses'`.
   * The Action denies login if the resolved account doesn't carry the named
   * subscription, so this also doubles as a cheap "is this user actually
   * licensed for this app?" check at the auth layer.
   *
   * <p>
   * OPTIONAL, for apps nobody subscribes to — My Hub is the account portal
   * every account has by definition, so there is no licence to check and
   * naming one would have the Action deny every login.
   * </p>
   */
  subscription?: string;
  /** Defaults to `openid profile email offline_access`. */
  scope?: string;
  /** Defaults to `window.location.origin`. */
  redirectUri?: string;
  /**
   * Extra authorization params merged into the redirect / silent-auth call.
   * Currently used for `ui_locales` (passed by the consuming app from its
   * resolved boot language so Auth0's universal login renders in the right
   * language). Keys collide-last-wins with the platform's own params.
   */
  extraAuthorizationParams?: Record<string, string>;
}

interface Props {
  settings: Auth0Settings;
  children: ReactNode;
}

/**
 * Wraps `<Auth0Provider>` so the redirect callback hands control back to
 * react-router's `navigate` instead of doing a full-page reload (which would
 * blow away the SPA state). All three apps consume this — they only differ
 * in the values they pass through `settings`.
 *
 * Multi-account wiring: reads `getStoredAccountId()` from the platform
 * account store and passes it to Auth0 as `account_id`. The PostLogin Action
 * picks it up and mints a JWT scoped to that account; if the cache is empty
 * (first ever device) or carries an account the user no longer has access
 * to, the Action falls back to the user's default. Always passes
 * `subscription` so the Action also gates by app licence.
 */
export function Auth0ProviderWithRouter({ settings, children }: Props) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname, { replace: true });
  };

  // Read the cache *once* at provider mount. Subsequent switches go through
  // `getAccessTokenSilently({ authorizationParams: { account_id: ... } })`,
  // which overrides this value at call time — so this is only the *boot*
  // hint. Empty string means "no preference, use default" (Action handles
  // that path).
  const cachedAccountId = getStoredAccountId();

  return (
    <Auth0Provider
      domain={settings.domain}
      clientId={settings.clientId}
      authorizationParams={{
        redirect_uri: settings.redirectUri ?? window.location.origin,
        audience: settings.audience,
        scope: settings.scope ?? 'openid profile email offline_access',
        // Omitted entirely when the app has no subscription to gate on —
        // sending an empty one would fail the Action's truthiness check.
        ...(settings.subscription ? { subscription: settings.subscription } : {}),
        // Only set `account_id` when we actually have a hint. Auth0 ignores
        // undefined; passing an empty string would surface in the request
        // body and might confuse the Action (which checks truthiness).
        ...(cachedAccountId ? { account_id: cachedAccountId } : {}),
        ...(settings.extraAuthorizationParams ?? {}),
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
